'use client';

import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { toErrorMessage } from '@/lib/error-utils';
import { Button } from '@/components/ui/button';
import { TurnstileWidget, isTurnstileConfiguredClientSide } from '@/components/TurnstileWidget';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const captchaRequired = isTurnstileConfiguredClientSide();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // A disabled submit <button> does not stop Enter-key submission from a
    // text input inside the same <form> — that bypasses the button entirely
    // and fires onSubmit directly. Guard here too, so a not-yet-solved
    // CAPTCHA gets a clear inline message instead of a failed API round trip.
    if (captchaRequired && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(toErrorMessage(data.error, 'Failed to send reset link'));
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-100 dark:from-primary-950/30 dark:to-indigo-950/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link href="/auth/signin" className="inline-flex items-center text-primary hover:text-primary-700 mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to sign in
          </Link>
          
          <div className="flex items-center justify-center mb-6">
            <Image
              src="/brand/delfin-logo-full.svg"
              alt="Delfin"
              width={200}
              height={40}
              priority
              className="h-8 w-auto dark:hidden"
            />
            <Image
              src="/brand/delfin-logo-full-ivory.svg"
              alt="Delfin"
              width={200}
              height={40}
              priority
              className="h-8 w-auto hidden dark:block"
            />
          </div>
        </div>

        {/* Form */}
        <div className="max-w-md mx-auto bg-card p-8 rounded-lg shadow-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground">
              {success ? 'Check Your Email' : 'Reset Password'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {success 
                ? 'We sent you a password reset link' 
                : 'Enter your email to receive a password reset link'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success ? (
            <div className="text-center space-y-4">
              <div className="text-6xl">📧</div>
              <div>
                <p className="text-muted-foreground">
                  We sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Click the link in your email to reset your password. The link will expire in 1 hour.
                </p>
              </div>
              <Link
                href="/auth/signin"
                className="inline-block w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 text-center"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={isLoading}
                />
              </div>
              <TurnstileWidget
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
              />

              <Button
                type="submit"
                disabled={isLoading || !email || (captchaRequired && !turnstileToken)}
                isLoading={isLoading}
                className="w-full bg-primary-600 text-white hover:bg-primary-700"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}

          {!success && (
            <div className="mt-4 text-center text-sm">
              <p>
                Remember your password?{' '}
                <Link href="/auth/signin" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
